import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';

export interface FileItem {
  id: string;
  name: string;
  sizeBytes: number;
  mimeType: string | null;
  isShared: boolean;
  shareToken: string | null;
  folderId: string | null;
  uploadStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface FolderItem {
  id: string;
  name: string;
  parentFolderId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FilesData {
  files: FileItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface FoldersData {
  folders: FolderItem[];
}

export function useFiles(folderId: string | null) {
  const params = new URLSearchParams();
  if (folderId) params.set('folderId', folderId);

  return useQuery({
    queryKey: ['files', folderId],
    queryFn: () => apiGet<FilesData>(`/files?${params.toString()}`),
    select: (res) => (res.success ? res.data : undefined),
  });
}

export function useSearchFiles(search: string) {
  const trimmed = search.trim();
  return useQuery({
    queryKey: ['files', 'search', trimmed],
    queryFn: () => apiGet<FilesData>(`/files?search=${encodeURIComponent(trimmed)}`),
    select: (res) => (res.success ? res.data : undefined),
    enabled: trimmed.length > 0,
  });
}

// parentFolderId is required (not optional) so this hook's query key can never collide
// with useAllFolders() below — JSON.stringify(['folders', undefined]) and
// JSON.stringify(['folders', null]) both serialize to '["folders",null]', so an
// optional/omitted parentFolderId here would silently share React Query's cache
// entry with the root-level (parentFolderId=null) view.
export function useFolders(parentFolderId: string | null) {
  const params = new URLSearchParams();
  params.set('parentFolderId', parentFolderId ?? '');

  return useQuery({
    queryKey: ['folders', parentFolderId],
    queryFn: () => apiGet<FoldersData>(`/folders?${params.toString()}`),
    select: (res) => (res.success ? res.data : undefined),
  });
}

// Fetches the user's entire folder list (no parentFolderId filter) — used by the
// move-destination picker. Kept as a separate hook/query key from useFolders() so it
// can't collide with any per-folder cache entry (see note above).
export function useAllFolders() {
  return useQuery({
    queryKey: ['folders', 'all'],
    queryFn: () => apiGet<FoldersData>('/folders'),
    select: (res) => (res.success ? res.data : undefined),
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; parentFolderId?: string | null }) =>
      apiPost<FolderItem>('/folders', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiPatch<FolderItem>(`/folders/${id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
}

export function useMoveFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentFolderId }: { id: string; parentFolderId: string | null }) =>
      apiPatch<FolderItem>(`/folders/${id}`, { parentFolderId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/folders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['files'] });
    },
  });
}

export function useRenameFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiPatch<FileItem>(`/files/${id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

export function useMoveFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      apiPatch<FileItem>(`/files/${id}`, { folderId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/files/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

export function useBulkDeleteFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileIds: string[]) =>
      apiPost<{ deletedCount: number }>('/files/bulk-delete', { fileIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

export function useBulkMoveFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileIds, folderId }: { fileIds: string[]; folderId: string | null }) =>
      apiPost<{ movedCount: number }>('/files/bulk-move', { fileIds, folderId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

interface ShareResult {
  shareToken: string;
  shareUrl: string;
  expiresAt: string | null;
}

export function useShareFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: string; expiresAt?: string | null }) =>
      apiPost<ShareResult>(`/files/${id}/share`, { expiresAt: expiresAt ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}

export function useRevokeShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/files/${id}/share`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files'] }),
  });
}
